import { Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DomainService } from '../../services/domain.service';
import { Domain } from '../../models/domain';
import { Subscription } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { ToastContainerDirective } from 'ngx-toastr';
import { AtlasToastService } from '../../services/atlas-toast.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
// import {UserService} from '../../services/user.service';
import { ProfileService } from '../../services/profile.service';
import { RegisterService } from '@services/register.service';
import { nations } from '../../utils/constants';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import 'moment-timezone';
import { LoginService } from '../../core/login/login.service';
import { validateFormGroup } from '@utils/utils';
import { SessionStorageService } from 'ngx-webstorage';
import { CookieService } from 'ngx-cookie';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { checkPassword } from '@utils/validators';

function matchingPasswords(matchTo: string): (AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    return !!control.parent && !!control.parent.value && control.value === control.parent.get(matchTo).value
      ? null
      : { notEquivalent: true };
  };
}

@Component({
  selector: 'wsb-register-component',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class RegisterComponent implements OnInit, OnDestroy {
  @ViewChild('username') username?: ElementRef;
  @ViewChild('modalReset') modalReset;
  modalResetModal: NgbModalRef;
  @ViewChild(ToastContainerDirective) toastContainer: ToastContainerDirective;
  resetForm: UntypedFormGroup = this.fb.group({
    username: ['', [Validators.required]]
  });
  faPasswordIconShow = faEye;
  showPassword = false;
  faPasswordConfirmIconShow = faEye;
  showPasswordConfirm = false;
  registerForm = this.fb.group({
    nat: ['', [Validators.required]],
    firstname: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(49)]],
    lastname: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(49)]],
    email: ['', [Validators.required, Validators.email]],
    accountType: ['COMPANY'],
    company: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
    username: [''],
    password: ['', [Validators.required, checkPassword()]],
    passwordConfirm: ['', [Validators.required, matchingPasswords('password')]],
    taxcode: ['', []],
    checkConditions1: ['', [Validators.required]],
    checkConditions2: ['', [Validators.required]],
    checkPrivacy: ['', [Validators.required]],
    checkMarketing: ['']
  });

  verifyNumberForm = this.fb.group({
    phoneNumber: ['', [Validators.required, Validators.pattern(/^(\+)?[0-9]{5,15}$/)]]
  });

  verifyCodeForm = this.fb.group({
    verifyCode: ['', [Validators.required]]
  });

  domain: Domain;
  loadingStep1 = false;
  loadingStep2 = false;
  loadingStep3 = false;
  otpSent = false;
  nations;
  ac;
  isBuying = false;
  registrationStep = 1;
  mainNation;
  otpSendTries = 0;
  codeValid = false;
  codeInvalid = false;
  maxOtpSent = 3;
  public removeEventListener: () => void;
  hasCheckConditions2 = true;
  hasCheckPrivacy = true;

  constructor(
    private registerService: RegisterService,
    private translate: TranslateService,
    private router: Router,
    private fb: UntypedFormBuilder,
    private domainService: DomainService,
    // private userService: UserService,
    private profileService: ProfileService,
    private atlasToastService: AtlasToastService,
    private modalService: NgbModal,
    private loginService: LoginService,
    private renderer: Renderer2,
    private translateService: TranslateService,
    private sessionStorage: SessionStorageService,
    private elementRef: ElementRef,
    private cookieService: CookieService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.domainService.getDomainState().subscribe(x => (this.domain = x));

    this.nations = nations.map(nat => ({ label: nat[2] + ', ' + nat[1], value: nat[0] }));

    this.registerForm.get('email').valueChanges.subscribe(value => {
      this.registerForm.get('username').setValue(value);
    });

    this.route.queryParams.subscribe(query => {
      this.ac = query.ac || query.ref || null;
      if (query.nat) {
        this.registerForm.get('nat').patchValue(query.nat);
        this.mainNation = query.nat;
      }
      if (query.lang) {
        this.translate.use(query.lang); // decodeLanguage
      }

      this.translateService.get('register_main_nation').subscribe(label => {
        if (label && label !== '' && label !== 'register_main_nation') {
          this.registerForm.get('nat').patchValue(label);
          this.mainNation = label;
        }
      });
      this.translateService.onLangChange.pipe(switchMap(() => this.translateService.get('register_main_nation'))).subscribe(label => {
        this.registerForm.get('nat').patchValue(label);
        this.mainNation = label;
      });
    });

    this.removeEventListener = this.renderer.listen(this.elementRef.nativeElement, 'click', event => {
      if (event.target instanceof HTMLAnchorElement) {
        // Your custom anchor click event handler
        this.handleAnchorClick(event);
      }
    });

    if (this.isBuying) {
      this.registerForm.get('taxcode').setValidators([Validators.required]);
    }

    if (this.sessionStorage.retrieve('wsb-user-status')) {
      if (this.sessionStorage.retrieve('wsb-user-status') !== 'REGISTERED' || !this.sessionStorage.retrieve('wsb-temp-credentials')) {
        this.loginService.redirectToLogin();
      } else {
        this.registrationStep = 2;
      }
    }

    this.translateService.get('register_check_terms_2').subscribe(label => {
      this.hasCheckConditions2 = label && label !== '' && label !== 'register_check_terms_2';
      this.updateValidation();
    });
    this.translateService.onLangChange.pipe(switchMap(() => this.translateService.get('register_check_terms_2'))).subscribe(label => {
      this.hasCheckConditions2 = label && label !== '' && label !== 'register_check_terms_2';
      this.updateValidation();
    });
    this.translateService.get('check_privacy').subscribe(label => {
      this.hasCheckPrivacy = label && label !== '' && label !== 'check_privacy';
      this.updateValidation();
    });
    this.translateService.onLangChange.pipe(switchMap(() => this.translateService.get('check_privacy'))).subscribe(label => {
      this.hasCheckPrivacy = label && label !== '' && label !== 'check_privacy';
      this.updateValidation();
    });
  }

  updateValidation() {
    this.registerForm.get('checkConditions2').setValidators(this.hasCheckConditions2 ? [Validators.required] : []);
    this.registerForm.get('checkPrivacy').setValidators(this.hasCheckPrivacy ? [Validators.required] : []);
  }

  openModalReset(e?: any): void {
    this.modalResetModal = this.modalService.open(this.modalReset);
    // this.modalResetModal.result.then(
    //   result => {},
    //   reason => {}
    // );
  }

  modalResetForm(): void {
    if (!this.resetForm.valid) {
      return;
    }
    this.resetForm.disable();
    const raw = this.resetForm.getRawValue();
    this.profileService.resetSend(raw.username).subscribe(
      () => {
        this.resetForm.reset();
        this.atlasToastService.success('register_passreset_success_title', 'register_passreset_success_text', 600000, this.toastContainer);
        this.resetForm.enable();
      },
      () => {
        this.atlasToastService.error('common_error', null);
        this.resetForm.enable();
      },
      () => {}
    );
    this.modalResetModal.close();
  }

  cancel(): void {
    this.registerForm.patchValue({
      username: '',
      password: ''
    });
  }

  disableSubmit() {
    if (this.registerForm.get('checkConditions1').value !== true) {
      return true;
    }
    if (this.hasCheckConditions2 && this.registerForm.get('checkConditions2').value !== true) {
      return true;
    }
    if (this.hasCheckPrivacy && this.registerForm.get('checkPrivacy').value !== true) {
      return true;
    }
    return false;
  }

  register(): Subscription {
    if (!this.registerForm.valid) {
      validateFormGroup(this.registerForm);
      return;
    }
    this.registerForm.disable();

    this.loadingStep1 = true;
    const data: any = {
      userType: this.registerForm.get('accountType').value ? this.registerForm.get('accountType').value : 'COMPANY',
      idNation: this.registerForm.get('nat').value,
      name: this.registerForm.get('firstname').value,
      surname: this.registerForm.get('lastname').value,
      email: this.registerForm.get('email').value,
      username: this.registerForm.get('username').value,
      password: this.registerForm.get('password').value,
      // publicCartUserBookmark: '9a6d430466ac140581ea01576',
      company: this.registerForm.get('company').value,
      // vatNumber: this.registerForm.get('password').value,
      enableMarketing: this.registerForm.get('checkMarketing').value ? true : false,
      timezone: moment.tz.guess()
      // companyReferrer: 'Raziolalist'
    };
    // this.handlerForm.get('action.auto.autoSender').setValidators([Validators.required]);
    if (this.registerForm.get('taxcode').value) {
      data.fiscalCode = this.registerForm.get('taxcode').value;
    }
    if (this.ac) {
      data.partnerCode = this.ac;
    }
    data.leadSource = {};
    if (this.cookieService.get('ls')) {
      data.leadSource.ls = this.cookieService.get('ls');
    }
    if (this.cookieService.get('sc')) {
      data.leadSource.sc = this.cookieService.get('sc');
    }
    if (this.cookieService.get('sd')) {
      data.leadSource.sd = this.cookieService.get('sd');
    }
    // query params
    if (this.route.snapshot.queryParams['ls']) {
      data.leadSource.ls = this.route.snapshot.queryParams['ls'];
    }
    if (this.route.snapshot.queryParams['sc']) {
      data.leadSource.sc = this.route.snapshot.queryParams['sc'];
    }
    if (this.route.snapshot.queryParams['sd']) {
      data.leadSource.sd = this.route.snapshot.queryParams['sd'];
    }
    return this.registerService
      .register(data)
      .pipe(finalize(() => (this.loadingStep1 = false)))
      .subscribe(
        (data2: any) => {
          this.loginService.storeAuthData(data2);
          this.sessionStorage.store('wsb-temp-credentials', {
            username: this.registerForm.get('username').value,
            password: this.registerForm.get('password').value
          });
          // this.userService.identity(true).subscribe(angularUser => {
          //   if (angularUser === null) {
          //   }
          // });
          // this.userService.getAuthenticationState().subscribe(loggedUser => {
          //   console.log('logged', loggedUser);
          // });
          this.registrationStep = 2;

          if ((window as any).gtagConfig) {
            const path = '/register-verify-number';
            (window as any).gtagConfig({ page_path: path });
            //ga4
            if ((window as any).gtagEvent) {
              (window as any).gtagEvent('page_view', { page_title: 'Register Step 2', page_location: location.origin + path });
            }
          }

          // this.loginService // disable later, after dismissing of php
          //   .login({
          //     username: this.registerForm.controls.username.value,
          //     password: this.registerForm.controls.password.value
          //   }).subscribe(() => {
          //   this.registrationStep = 2;
          //
          // });
        },
        e => {
          this.atlasToastService.error(
            e.error && e.error.error_message ? e.error.error_message : 'register_register_error',
            null,
            600000,
            this.toastContainer
          );
        }
      );
  }

  verifyNumber(): Subscription {
    if (!this.verifyNumberForm.valid) {
      validateFormGroup(this.verifyNumberForm);
      return;
    }
    this.loadingStep2 = true;
    return this.registerService
      .sendOtp(this.verifyNumberForm.get('phoneNumber').value)
      .pipe(finalize(() => (this.loadingStep2 = false)))
      .subscribe(
        () => {
          this.registrationStep = 3;
          this.otpSendTries++;
          this.otpSent = true;
        },
        e => {
          this.atlasToastService.error(
            e.error && e.error.error_message ? e.error.error_message : 'register_register_error',
            null,
            600000,
            this.toastContainer
          );
        }
      );
  }

  verifyCode(): Subscription {
    if (!this.verifyCodeForm.valid) {
      validateFormGroup(this.verifyCodeForm);
      return;
    }
    this.loadingStep3 = true;
    return this.registerService
      .verifyOtp(this.verifyCodeForm.get('verifyCode').value)
      .pipe(finalize(() => (this.loadingStep3 = false)))
      .subscribe(
        () => {
          this.codeValid = true;
          if ((window as any).gtagConfig) {
            const path = '/register-access-ok';
            (window as any).gtagConfig({ page_path: path });
            //ga4
            if ((window as any).gtagEvent) {
              (window as any).gtagEvent('page_view', { page_title: 'Register Step 3', page_location: location.origin + path });
            }
          }
          // this.sessionStorage.store('wsb-user-status', 'ACTIVE');
          // Costretto a riforzare il login, altrimenti il frontend legacy php non cacha lo stato
          this.loginService
            .login(this.sessionStorage.retrieve('wsb-temp-credentials'))
            .subscribe(() => this.router.navigate(['dashboard']));
          // this.router.navigate(['dashboard']);
        },
        e => {
          this.codeInvalid = true;
          // this.atlasToastService.error(e.error && e.error.error_message ? e.error.error_message : 'register_register_error',
          //   null, 600000, this.toastContainer);
          this.atlasToastService.error('register_code_invalid', null, 600000, this.toastContainer);
        }
      );
  }

  requestResetPassword(): void {
    this.router.navigate(['/account/reset', 'request']);
  }

  public ngOnDestroy() {
    // Cleanup by removing the event listener on destroy
    this.removeEventListener();
  }

  public handleAnchorClick(event: Event) {
    // Prevent opening anchors the default way
    const anchor = event.target as HTMLAnchorElement;
    if (anchor.id && anchor.id === 'request-new-code') {
      event.preventDefault();
      this.verifyNumber();
    } else if (anchor.id && anchor.id === 'change-number') {
      event.preventDefault();
      this.registrationStep = 2;
      this.otpSent = false;
    }
  }

  showHidePassWord() {
    this.showPassword = !this.showPassword;
    if (this.showPassword) {
      this.faPasswordIconShow = faEyeSlash;
    } else {
      this.faPasswordIconShow = faEye;
    }
  }

  showHidePassWordConfirm() {
    this.showPasswordConfirm = !this.showPasswordConfirm;
    if (this.showPasswordConfirm) {
      this.faPasswordConfirmIconShow = faEyeSlash;
    } else {
      this.faPasswordConfirmIconShow = faEye;
    }
  }
}
